import React, { useState, useMemo, useEffect } from 'react';
import { HomeIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { FingerPrintIcon } from '@heroicons/react/24/solid';
import { Search } from 'lucide-react';
import FileCard from '../moduleComponents/FileCard';
import FolderCard from '../moduleComponents/FolderCard';

/** Entries (subfolders + files) at a path prefix. Supports nested folders. */
const getEntriesAt = (files, currentPath) => {
  const prefix = currentPath ? `${currentPath}/` : '';
  const folderCounts = new Map();
  const filesHere = [];

  files.forEach((file) => {
    const path = file.path || '';
    if (prefix) {
      if (!path.startsWith(prefix)) return;
      const rest = path.slice(prefix.length);
      if (!rest) return;
      const slash = rest.indexOf('/');
      if (slash === -1) {
        filesHere.push(file);
      } else {
        const name = rest.slice(0, slash);
        folderCounts.set(name, (folderCounts.get(name) || 0) + 1);
      }
      return;
    }

    const slash = path.indexOf('/');
    if (slash === -1) {
      filesHere.push(file);
    } else {
      const name = path.slice(0, slash);
      folderCounts.set(name, (folderCounts.get(name) || 0) + 1);
    }
  });

  const folders = [...folderCounts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name));

  filesHere.sort((a, b) => a.path.localeCompare(b.path));
  return { folders, filesHere };
};

const MainExplorer = ({
  files,
  isLoading,
  onFileClick,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  searchLoading,
  currentPath,
  onPathChange,
  onCreateClick,
  onUploadClick,
}) => {
  // Keep breadcrumb path in sync when parent resets (e.g. leaving search)
  const [localPath, setLocalPath] = useState(currentPath || '');
  useEffect(() => {
    if (typeof currentPath === 'string') setLocalPath(currentPath);
  }, [currentPath]);

  const path = typeof currentPath === 'string' ? currentPath : localPath;
  const setPath = (next) => {
    setLocalPath(next);
    onPathChange?.(next);
  };

  const { folders, filesHere } = useMemo(() => getEntriesAt(files, path), [files, path]);
  const segments = path ? path.split('/').filter(Boolean) : [];
  const isSearch = Boolean(searchQuery?.trim());
  const hasContent = folders.length > 0 || filesHere.length > 0;

  if (isLoading && !isSearch) {
    return (
      <div className="space-y-3 px-4 py-4" aria-busy="true" aria-label="Loading">
        <div className="h-8 w-full animate-pulse rounded-md bg-gray-100" />
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="flex h-12 items-center border-b border-gray-100 px-4 last:border-0">
              <div className="h-4 flex-1 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const searchField = (
    <div className="relative min-w-0 flex-1">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        type="search"
        value={searchQuery || ''}
        onChange={(e) => onSearchQueryChange?.(e.target.value)}
        placeholder="Search files by path"
        className="h-8 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-module-identity"
        aria-label="Search files by path"
      />
    </div>
  );

  if (isSearch) {
    return (
      <div className="space-y-3 px-4 py-4">
        <div className="flex items-center gap-2">{searchField}</div>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {searchLoading ? (
            <div aria-busy="true" aria-label="Loading">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="flex h-12 items-center border-b border-gray-100 px-4 last:border-0">
                  <div className="h-4 flex-1 animate-pulse rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : !searchResults?.length ? (
            <p className="py-8 text-center text-sm text-gray-400">No files found</p>
          ) : (
            searchResults.map((file) => (
              <FileCard key={file.path} file={file} onClick={onFileClick} showPath />
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 px-1">
        <div className="flex items-center gap-1 text-sm text-gray-500 flex-wrap min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setPath('')}
            className="flex items-center gap-1 hover:text-module-identity transition-colors shrink-0"
          >
            <HomeIcon className="h-4 w-4" />
            <span>Identity</span>
          </button>
          {segments.map((seg, idx) => {
            const target = segments.slice(0, idx + 1).join('/');
            const isLast = idx === segments.length - 1;
            return (
              <React.Fragment key={target}>
                <ChevronRightIcon className="h-3 w-3 shrink-0" />
                {isLast ? (
                  <span className="text-gray-800 font-medium truncate">{seg}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPath(target)}
                    className="hover:text-module-identity transition-colors truncate"
                  >
                    {seg}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="w-full sm:w-56 sm:shrink-0">{searchField}</div>
        <div className="flex items-center gap-1 shrink-0">
          {onUploadClick && (
            <button
              type="button"
              onClick={() => onUploadClick(path)}
              className="text-xs font-medium text-module-identity hover:text-module-identity px-2 py-1 rounded-lg hover:bg-module-identity/10"
            >
              Upload
            </button>
          )}
          {onCreateClick && (
            <button
              type="button"
              onClick={() => onCreateClick(path)}
              className="text-xs font-medium text-module-identity hover:text-module-identity px-2 py-1 rounded-lg hover:bg-module-identity/10"
            >
              + New file
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {!hasContent ? (
          <div className="text-center py-12 text-gray-400">
            <FingerPrintIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">{path ? 'Empty folder' : 'No files yet'}</p>
            <div className="mt-3 flex items-center justify-center gap-3">
              {onUploadClick && (
                <button
                  type="button"
                  onClick={() => onUploadClick(path)}
                  className="text-sm font-medium text-module-identity hover:text-module-identity"
                >
                  Upload files
                </button>
              )}
              {onCreateClick && (
                <button
                  type="button"
                  onClick={() => onCreateClick(path)}
                  className="text-sm font-medium text-module-identity hover:text-module-identity"
                >
                  Create a file
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {folders.map(({ name, count }) => (
              <FolderCard
                key={name}
                name={name}
                count={count}
                onClick={() => setPath(path ? `${path}/${name}` : name)}
              />
            ))}
            {filesHere.map((file) => (
              <FileCard key={file.path} file={file} onClick={onFileClick} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default MainExplorer;
